import type { Editor as EditorInstance } from "@tiptap/core";
import { useEditorState } from "@tiptap/react";
import { type ReactNode } from "react";

import { safeUrl } from "./serialize";

/**
 * The WYSIWYG toolbar (SPEC §14).
 *
 * Every button maps to one ProseMirror command over the document — the toolbar
 * is pure UI over the same extension set that defines what the document *is*.
 * That boundary is also what keeps it honest with the published format
 * (AGENTS.md §11): the toolbar offers exactly the formatting the Markdown
 * serializer can carry, nothing that would silently vanish at publish time.
 *
 * Labels are injectable so a host can localize (the Admin passes its `t()`
 * output); the English defaults keep the package usable without one.
 */

/** Every label the toolbar can render, keyed by what the button does. */
export interface ToolbarLabels {
  readonly undo?: string;
  readonly redo?: string;
  readonly paragraph?: string;
  readonly heading1?: string;
  readonly heading2?: string;
  readonly heading3?: string;
  readonly heading4?: string;
  readonly bold?: string;
  readonly italic?: string;
  readonly underline?: string;
  readonly strike?: string;
  readonly code?: string;
  readonly clearFormat?: string;
  readonly bulletList?: string;
  readonly orderedList?: string;
  readonly taskList?: string;
  readonly blockquote?: string;
  readonly codeBlock?: string;
  readonly horizontalRule?: string;
  readonly link?: string;
  readonly linkPrompt?: string;
  readonly table?: string;
  readonly tableRow?: string;
  readonly tableRowDelete?: string;
  readonly tableColumn?: string;
  readonly tableColumnDelete?: string;
  readonly tableHeaderRow?: string;
  readonly tableDelete?: string;
}

const DEFAULT_LABELS: Required<ToolbarLabels> = {
  undo: "Undo",
  redo: "Redo",
  paragraph: "Text",
  heading1: "Heading 1",
  heading2: "Heading 2",
  heading3: "Heading 3",
  heading4: "Heading 4",
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  strike: "Strikethrough",
  code: "Inline code",
  clearFormat: "Clear formatting",
  bulletList: "Bulleted list",
  orderedList: "Numbered list",
  taskList: "Task list",
  blockquote: "Quote",
  codeBlock: "Code block",
  horizontalRule: "Horizontal rule",
  link: "Link",
  linkPrompt: "Link URL",
  table: "Insert table",
  tableRow: "Insert row",
  tableRowDelete: "Delete row",
  tableColumn: "Insert column",
  tableColumnDelete: "Delete column",
  tableHeaderRow: "Toggle header row",
  tableDelete: "Delete table",
};

interface ToolbarState {
  readonly undo: boolean;
  readonly redo: boolean;
  readonly paragraph: boolean;
  readonly headingLevel: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strike: boolean;
  readonly code: boolean;
  readonly bulletList: boolean;
  readonly orderedList: boolean;
  readonly taskList: boolean;
  readonly blockquote: boolean;
  readonly codeBlock: boolean;
  readonly link: boolean;
  readonly table: boolean;
}

/** The WYSIWYG toolbar for one editor instance. Host page supplies labels. */
export function EditorToolbar({
  editor,
  labels,
}: {
  readonly editor: EditorInstance;
  readonly labels?: ToolbarLabels;
}) {
  const t: Required<ToolbarLabels> = { ...DEFAULT_LABELS, ...labels };

  const state = useEditorState<ToolbarState>({
    editor,
    selector: ({ editor: instance }) => ({
      undo: instance.can().undo(),
      redo: instance.can().redo(),
      paragraph: instance.isActive("paragraph") && !instance.isActive("heading"),
      headingLevel: instance.isActive("heading")
        ? ((instance.getAttributes("heading").level as number | undefined) ?? 1)
        : 0,
      bold: instance.isActive("bold"),
      italic: instance.isActive("italic"),
      underline: instance.isActive("underline"),
      strike: instance.isActive("strike"),
      code: instance.isActive("code"),
      bulletList: instance.isActive("bulletList"),
      orderedList: instance.isActive("orderedList"),
      taskList: instance.isActive("taskList"),
      blockquote: instance.isActive("blockquote"),
      codeBlock: instance.isActive("codeBlock"),
      link: instance.isActive("link"),
      table: instance.isActive("table"),
    }),
  });

  const inTable = state?.table === true;

  function applyHeading(level: number): void {
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 | 4 }).run();
    }
  }

  function toggleLink(): void {
    if (state?.link === true) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    const href = safeUrl(window.prompt(t.linkPrompt) ?? "");

    if (href === null) {
      return;
    }

    if (editor.state.selection.empty) {
      // Nothing selected: the URL becomes the link's text, like a pasted URL.
      editor
        .chain()
        .focus()
        .insertContent([
          { type: "text", text: href, marks: [{ type: "link", attrs: { href } }] },
          { type: "text", text: " " },
        ])
        .run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    }
  }

  return (
    <div
      role="toolbar"
      className="sticky top-0 z-10 mb-3 flex flex-wrap items-center gap-0.5 rounded-md border border-neutral-200 bg-white/95 p-1 shadow-sm backdrop-blur"
    >
      <ToolbarButton label={t.undo} disabled={state?.undo !== true} onClick={() => editor.chain().focus().undo().run()}>
        <IconUndo />
      </ToolbarButton>
      <ToolbarButton label={t.redo} disabled={state?.redo !== true} onClick={() => editor.chain().focus().redo().run()}>
        <IconRedo />
      </ToolbarButton>

      <Divider />

      {/* Paragraph format: what Word calls the style box, sized to Markdown. */}
      <select
        aria-label={t.paragraph}
        title={t.paragraph}
        value={state === null ? 0 : state.headingLevel === 0 ? -1 : state.headingLevel}
        onChange={(event) => {
          applyHeading(Number(event.target.value));
        }}
        className="h-7 rounded border border-neutral-200 bg-white px-1.5 text-xs text-neutral-700 focus:outline-none focus:ring-1 focus:ring-neutral-400"
      >
        <option value={-1}>{t.paragraph}</option>
        <option value={1}>{t.heading1}</option>
        <option value={2}>{t.heading2}</option>
        <option value={3}>{t.heading3}</option>
        <option value={4}>{t.heading4}</option>
      </select>

      <Divider />

      <ToolbarButton label={t.bold} active={state?.bold === true} onClick={() => editor.chain().focus().toggleBold().run()}>
        <IconBold />
      </ToolbarButton>
      <ToolbarButton label={t.italic} active={state?.italic === true} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <IconItalic />
      </ToolbarButton>
      <ToolbarButton
        label={t.underline}
        active={state?.underline === true}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <IconUnderline />
      </ToolbarButton>
      <ToolbarButton label={t.strike} active={state?.strike === true} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <IconStrike />
      </ToolbarButton>
      <ToolbarButton label={t.code} active={state?.code === true} onClick={() => editor.chain().focus().toggleCode().run()}>
        <IconCode />
      </ToolbarButton>
      <ToolbarButton
        label={t.clearFormat}
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
      >
        <IconEraser />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label={t.bulletList}
        active={state?.bulletList === true}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <IconBulletList />
      </ToolbarButton>
      <ToolbarButton
        label={t.orderedList}
        active={state?.orderedList === true}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <IconOrderedList />
      </ToolbarButton>
      <ToolbarButton
        label={t.taskList}
        active={state?.taskList === true}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <IconTaskList />
      </ToolbarButton>
      <ToolbarButton
        label={t.blockquote}
        active={state?.blockquote === true}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <IconBlockquote />
      </ToolbarButton>
      <ToolbarButton
        label={t.codeBlock}
        active={state?.codeBlock === true}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <IconCodeBlock />
      </ToolbarButton>
      <ToolbarButton label={t.horizontalRule} onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <IconHorizontalRule />
      </ToolbarButton>

      <Divider />

      <ToolbarButton label={t.link} active={state?.link === true} onClick={toggleLink}>
        <IconLink />
      </ToolbarButton>
      <ToolbarButton
        label={t.table}
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      >
        <IconTable />
      </ToolbarButton>

      {/* Table structure commands only exist with the caret inside a table. */}
      {inTable ? (
        <>
          <ToolbarButton label={t.tableRow} onClick={() => editor.chain().focus().addRowAfter().run()}>
            <IconRowPlus />
          </ToolbarButton>
          <ToolbarButton label={t.tableRowDelete} onClick={() => editor.chain().focus().deleteRow().run()}>
            <IconRowMinus />
          </ToolbarButton>
          <ToolbarButton label={t.tableColumn} onClick={() => editor.chain().focus().addColumnAfter().run()}>
            <IconColPlus />
          </ToolbarButton>
          <ToolbarButton label={t.tableColumnDelete} onClick={() => editor.chain().focus().deleteColumn().run()}>
            <IconColMinus />
          </ToolbarButton>
          <ToolbarButton label={t.tableHeaderRow} onClick={() => editor.chain().focus().toggleHeaderRow().run()}>
            <IconHeaderRow />
          </ToolbarButton>
          <ToolbarButton label={t.tableDelete} onClick={() => editor.chain().focus().deleteTable().run()}>
            <IconTrash />
          </ToolbarButton>
        </>
      ) : null}
    </div>
  );
}

/** One toolbar button: icon-only, labelled for pointer and screen reader. */
function ToolbarButton({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  readonly label: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-7 min-w-7 items-center justify-center rounded px-1 text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 disabled:pointer-events-none disabled:opacity-30 ${
        active ? "bg-neutral-900 !text-white hover:bg-neutral-900 hover:!text-white" : ""
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span aria-hidden="true" className="mx-1 h-4 w-px bg-neutral-200" />;
}

/** Stroke icons, drawn to the same hand as the Admin's rail icons. */
function Svg({ children }: { readonly children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function IconUndo() {
  return (
    <Svg>
      <path d="M3 7v6h6" />
      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
    </Svg>
  );
}

function IconRedo() {
  return (
    <Svg>
      <path d="M21 7v6h-6" />
      <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
    </Svg>
  );
}

function IconBold() {
  return (
    <Svg>
      <path d="M6 4h8a4 4 0 0 1 0 8H6z" />
      <path d="M6 12h9a4 4 0 0 1 0 8H6z" />
    </Svg>
  );
}

function IconItalic() {
  return (
    <Svg>
      <line x1="19" y1="4" x2="10" y2="4" />
      <line x1="14" y1="20" x2="5" y2="20" />
      <line x1="15" y1="4" x2="9" y2="20" />
    </Svg>
  );
}

function IconUnderline() {
  return (
    <Svg>
      <path d="M6 4v6a6 6 0 0 0 12 0V4" />
      <line x1="4" y1="20" x2="20" y2="20" />
    </Svg>
  );
}

function IconStrike() {
  return (
    <Svg>
      <path d="M16 4H9a3 3 0 0 0-2.83 4" />
      <path d="M14 12a4 4 0 0 1 0 8H6" />
      <line x1="4" y1="12" x2="20" y2="12" />
    </Svg>
  );
}

function IconCode() {
  return (
    <Svg>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </Svg>
  );
}

function IconEraser() {
  return (
    <Svg>
      <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
      <path d="M22 21H7" />
      <path d="m5 11 9 9" />
    </Svg>
  );
}

function IconBulletList() {
  return (
    <Svg>
      <line x1="9" y1="6" x2="20" y2="6" />
      <line x1="9" y1="12" x2="20" y2="12" />
      <line x1="9" y1="18" x2="20" y2="18" />
      <circle cx="4" cy="6" r="1" fill="currentColor" />
      <circle cx="4" cy="12" r="1" fill="currentColor" />
      <circle cx="4" cy="18" r="1" fill="currentColor" />
    </Svg>
  );
}

function IconOrderedList() {
  return (
    <Svg>
      <line x1="10" y1="6" x2="20" y2="6" />
      <line x1="10" y1="12" x2="20" y2="12" />
      <line x1="10" y1="18" x2="20" y2="18" />
      <text x="3" y="8" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
        1
      </text>
      <text x="3" y="14" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
        2
      </text>
      <text x="3" y="20" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
        3
      </text>
    </Svg>
  );
}

function IconTaskList() {
  return (
    <Svg>
      <rect x="3" y="4" width="5" height="5" rx="1" />
      <path d="m4 6.5 1.2 1.2L7.5 5.4" />
      <rect x="3" y="14" width="5" height="5" rx="1" />
      <line x1="11" y1="6.5" x2="21" y2="6.5" />
      <line x1="11" y1="16.5" x2="21" y2="16.5" />
    </Svg>
  );
}

function IconBlockquote() {
  return (
    <Svg>
      <path d="M6 17h3l2-4V7H5v6h3z" />
      <path d="M14 17h3l2-4V7h-6v6h3z" />
    </Svg>
  );
}

function IconCodeBlock() {
  return (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <polyline points="10 9 7.5 12 10 15" />
      <polyline points="14 9 16.5 12 14 15" />
    </Svg>
  );
}

function IconHorizontalRule() {
  return (
    <Svg>
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="7" y1="6" x2="17" y2="6" opacity="0.4" />
      <line x1="7" y1="18" x2="17" y2="18" opacity="0.4" />
    </Svg>
  );
}

function IconLink() {
  return (
    <Svg>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Svg>
  );
}

function IconTable() {
  return (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </Svg>
  );
}

function IconRowPlus() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <path d="M12 13.5v4M10 15.5h4" />
    </Svg>
  );
}

function IconRowMinus() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <path d="M10 16h4" />
    </Svg>
  );
}

function IconColPlus() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="12" y1="5" x2="12" y2="19" />
      <path d="M14.5 12h4M16.5 10v4" />
    </Svg>
  );
}

function IconColMinus() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="12" y1="5" x2="12" y2="19" />
      <path d="M14.5 12h4" />
    </Svg>
  );
}

function IconHeaderRow() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <rect x="3" y="5" width="18" height="5" rx="1" fill="currentColor" opacity="0.3" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </Svg>
  );
}

function IconTrash() {
  return (
    <Svg>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Svg>
  );
}
